import { createNlsMiddleware, lccstate, searchOpenProcess, userstate } from 'lcc-state';
import { logSystem } from 'logger';
import { publish, subscribe } from 'pub-sub';
import has from '@dojo/framework/core/has';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { DNode } from '@dojo/framework/core/interfaces';
import { createBreakpointMiddleware } from '@dojo/framework/core/middleware/breakpoint';
import { focus } from '@dojo/framework/core/middleware/focus';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { create, v, w } from '@dojo/framework/core/vdom';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { theme } from '@dojo/widgets/middleware/theme';
import Tooltip, { Orientation } from '@dojo/widgets/tooltip';
import { SpeedDial } from '@holmescorp/base-widgets/speed-dial';
import { MenuAction } from '@holmescorp/base-widgets/speed-dial/action';
import { NotificationIcon } from '@holmescorp/base-widgets/speed-dial/notification-icon';
import { Keys, convertToBoolean } from '@holmescorp/common';
import { MenuItem } from '@holmescorp/lcc-state/interfaces';
import { mayTriggerML } from '@holmescorp/ml';
import { resourceDitabookSelector } from '@holmescorp/resource-parser';

import css from '../css/heading.m.css';

// eslint-disable-next-line @typescript-eslint/no-unused-vars

interface HeadingIcache {
	onlinestate: boolean;
	useSearch: boolean;
	height: number;
	menuOpen: boolean;
	hasNotifications: boolean;
	notificationArea: string;
	//
	ttExplore: boolean;
	ttToolsandtips: boolean;
	ttGlossary: boolean;
	ttSearch: boolean;
	ttSettings: boolean;
	ttExamCountdownOpen: boolean;
	ttHome: boolean;
	//
	focusNode: string;
}

export interface HeadingProperties {
	showNav?: boolean;
}

const breakpoint = createBreakpointMiddleware({ mobile: 0, fullsize: 769 });
const icache = createICacheMiddleware<HeadingIcache>();
const nls = createNlsMiddleware('settings');
const factory = create({
	icache,
	theme,
	nls,
	lccstate,
	userstate,
	breakpoint,
	focus
}).properties<HeadingProperties>();
export const Heading = factory(
	({ properties, middleware: { icache, theme, nls, lccstate, userstate, breakpoint, focus } }) => {
		const { showNav = true } = properties();
		const onlinestate = icache.getOrSet('onlinestate', false);
		const speedDialOpen = icache.getOrSet('menuOpen', false);
		const hasNotifications = icache.getOrSet('hasNotifications', false);
		const notificationArea = icache.getOrSet('notificationArea', '');
		const { get, path } = lccstate;
		const online = get(path('online'));
		const product = get(path('product'));
		const mainmenu: MenuItem[] = get(path('mainmenu'));
		const bp = breakpoint.get('header');

		const { get: userget, path: userpath } = userstate;
		const { extra, hide } = userget(userpath('toolstate', 'countdown')) || {
			complete: false,
			extra: undefined,
			hide: undefined
		};

		if (onlinestate !== online) {
			icache.set('onlinestate', online);
			logSystem.info(() => `${online ? 'WS online' : 'WS offline'}`);
		}

		const {
			isPlaceholder,
			messages: {
				alt_label = '[settings] alt_label',
				fieldset_examcountdown = '[settings] fieldset_examcountdown',
				include_exam_countdown = 'false',
				stickyHeader,
				alt: altLabel,
				tt: ttLabel,
				help_url,
				msp_home,
				instructor_led_widgets = '[]'
			}
		} = nls();

		if (isPlaceholder) {
			return null;
		}
		const instructorish: boolean = window.hcstatic.instructorish ?? false;
		const instructorLedWidgets = JSON.parse(instructor_led_widgets);

		subscribe('notifications/change-status', (topic, status) => {
			const { hasNewNotifications = false, newNotificationArea = '' } = status;
			icache.set('hasNotifications', hasNewNotifications);
			icache.set('notificationArea', newNotificationArea);
		});

		const sticky = stickyHeader === 'true';
		const themeCss = theme.classes(css);

		/* images need CDN */
		const productalt = `${alt_label}`;
		const mobile = bp && bp.breakpoint === 'mobile' ? '-mobile' : '';
		const productimg = `${window.hcstatic.portal_cdn}/js/images/${product}${mobile}.png`;
		/* title is the product image */
		const title = v('img', {
			classes: [themeCss.product],
			...formatAriaProperties({ label: productalt }),
			role: 'img',
			xqa: 'product',
			alt: productalt,
			src: productimg,
			onclick: (event) => {
				event && event.stopPropagation();
				publish('site/homepage');
			}
		});

		/* leading and trailing nodes */
		const leading = w(resourceDitabookSelector, { topic: 'header_content_before', classes: ['header_leading'] });
		const trailing = w(resourceDitabookSelector, { topic: 'header_content_after', classes: ['header_trailing'] });

		if (mainmenu.length === 0) {
			mainmenu.push({
				pane: { widget: 'GlossaryPane', params: {} },
				icon: { action: 'glossary/open', faname: 'briefcase', altText: 'glossary', ttText: 'Glossary' }
			});
			mainmenu.push({
				pane: { widget: 'SettingsPane', params: {} },
				icon: { action: 'settings/open', faname: 'cogs', altText: 'settings', ttText: 'Settings' }
			});
		}
		subscribe('search/open', () => {
			lccstate.run(searchOpenProcess)({ searchOpen: true });
		});
		const menupanes: DNode[] = [
			// add standard panes without menu items
			w('VideoPane', { videonum: '1', openaction: 'video/open' }),
			w('CertPane', {}),
			w('EvalPane', {})
		];

		/* actions are wrapped in a speed-dial */
		const focusNode = icache.getOrSet('focusNode', 'bars');
		const shouldFocus = focus.shouldFocus();
		const ttActive = () => {
			icache.set('ttExamCountdownOpen', true);
		};
		const ttHidden = () => {
			icache.set('ttExamCountdownOpen', false);
		};

		const mainMenuIcons = mainmenu.map((item) => {
			const { icon, pane } = item;

			const { action = '', faname = '', altText: alt = '', ttText: tt = '' } = item.icon || {};
			const altText = altLabel || alt;
			const ttText = ttLabel || tt;
			const isHighlighted = hasNotifications && item.pane && item.pane.widget === notificationArea;

			if (!instructorish && item.pane && instructorLedWidgets.includes(item.pane.widget)) {
				return null;
			}

			if (pane) {
				const { widget, params } = pane;
				menupanes.push(
					w(
						widget,
						Object.assign(
							{
								onClose: () => {
									icache.set(
										'focusNode',
										faname === 'cogs' ? (speedDialOpen ? 'cogs' : 'calendar') : faname
									);
									focus.focus();
								}
							},
							params
						)
					)
				);
			}

			return icon
				? w(MenuAction, {
						onClick: () => {
							publish(action);
						},
						onFocus: () => icache.set('focusNode', action),
						shouldFocus: focusNode === faname && shouldFocus,
						xqa: `menu-action ${action.split('/')[0]}`,
						faname,
						altText,
						ttText,
						isHighlighted
				  })
				: null;
		});

		if (help_url) {
			const action = 'help';
			const ttText = 'Help';
			const altText = 'help';
			const faname = 'question';
			mainMenuIcons.unshift(
				w(MenuAction, {
					onClick: () => {
						window.open(help_url);
					},
					onFocus: () => icache.set('focusNode', action),
					shouldFocus: focusNode === faname && shouldFocus,
					xqa: `menu-action ${action.split('/')[0]}`,
					faname,
					altText,
					ttText,
					isHighlighted: false
				})
			);
		}

		const msp_button = (() => {
			if (msp_home) {
				const msp_icon = JSON.parse(msp_home);
				const { action = '', faname = '', altText = '', ttText = '' } = msp_icon || {};
				const xqa = `home ${action.split('/')[0]}`;

				return v('div', { classes: [themeCss.action] }, [
					w(
						Tooltip,
						{
							open: icache.getOrSet('ttHome', false),
							orientation: Orientation.bottom
						},
						[
							{
								content: v(
									'span',
									{
										id: `menu-action-tooltip-${altText || 'msp'}`
									},
									[ttText]
								),
								trigger: v(
									'div',
									{
										classes: [themeCss.home],
										tabIndex: 0,
										xqa: xqa,
										role: 'button',
										...formatAriaProperties({
											label: altText,
											describedby: `menu-action-tooltip-${altText || 'msp'}`
										}),
										onclick: () => {
											mayTriggerML();
											publish(action);
										},
										onkeydown: (event) => {
											switch (event.key) {
												case Keys.Enter:
												case Keys.Space:
												case Keys.Spacebar:
													event.preventDefault();
													mayTriggerML();
													publish(action);
													break;
												case Keys.Escape:
													icache.set('ttHome', false);
													break;
											}
										},
										onmouseover: () => {
											icache.set('ttHome', true);
										},
										onmouseout: () => icache.set('ttHome', false),
										onfocus: () => {
											icache.set('focusNode', action);
											icache.set('ttHome', true);
										},
										onblur: () => {
											icache.set('ttHome', false);
										}
									},
									[
										v('span', { classes: ['fa-stack'] }, [
											v('i', { classes: ['fas', `fa-${faname}`] })
										])
									]
								)
							}
						]
					)
				]);
			}
			return null;
		})();

		const actions: DNode[] = [
			w(
				SpeedDial,
				{
					direction: 'left',
					faname: 'bars',
					onOpen: () => icache.set('menuOpen', true),
					onClose: () => {
						icache.set('focusNode', 'bars');
						icache.set('menuOpen', false);
					},
					stickyOpen: true
				},
				mainMenuIcons
			),
			hasNotifications && !speedDialOpen ? w(NotificationIcon, { isSpeedDialNotification: true }) : null
		];

		return v(
			'header',
			{
				key: 'header',
				role: 'banner',
				xqa: 'heading',
				classes: [theme.variant(), themeCss.root]
			},
			[
				v('div', { classes: [themeCss.heading, sticky && themeCss.sticky], key: 'root' }, [
					v('div', { classes: [themeCss.row] }, [
						v('div', { classes: [themeCss.primary], key: 'primary' }, [
							leading && v('div', { classes: [themeCss.leading] }, [leading]),
							v('div', { classes: [themeCss.title], key: 'title' }, [title])
						]),
						msp_button,
						convertToBoolean(include_exam_countdown) && !hide && !speedDialOpen && showNav
							? v('div', { classes: [themeCss.action] }, [
									w(
										Tooltip,
										{
											open: icache.getOrSet('ttExamCountdownOpen', false),
											orientation: Orientation.bottom
										},
										[
											{
												content: v(
													'span',
													{
														id: 'exam-countdown-tooltip'
													},
													[fieldset_examcountdown]
												),
												trigger: v(
													'div',
													{
														classes: [themeCss.examCalendar],
														xqa: 'exam-countdown-container',
														tabIndex: 0,
														role: 'button',
														...formatAriaProperties({
															describedby: 'exam-countdown-tooltip',
															label: 'Exam countdown'
														}),
														onclick: () => {
															publish('settings/open');
														},
														onfocus: () => {
															ttActive();
															icache.set('focusNode', 'settings/open');
														},
														onblur: () => ttHidden(),
														onmouseover: () => ttActive(),
														onmouseout: () => ttHidden(),
														focus: () => focusNode === 'calendar' && shouldFocus,
														onkeydown: (event) => {
															switch (event.key) {
																case Keys.Enter:
																case Keys.Space:
																case Keys.Spacebar:
																	event.preventDefault();
																	publish('settings/open');
																	break;
																case Keys.Escape:
																	ttHidden();
																	break;
															}
														}
													},
													[
														v('span', { classes: ['fa-stack'] }, [
															v('i', { classes: ['far', 'fa-calendar', 'fa-stack-2x'] }),
															v(
																'strong',
																{
																	classes: ['fa-stack-1x'],
																	xqa: 'exam-countdown-date'
																},
																[extra ? extra : '-']
															)
														])
													]
												)
											}
										]
									)
							  ])
							: null,
						showNav
							? v('div', { classes: [themeCss.secondary], key: 'secondary' }, [
									v(
										'nav',
										{ classes: [themeCss.actions], key: 'actions' },
										actions &&
											(Array.isArray(actions) ? actions : [actions]).map((action) =>
												v('div', { classes: [themeCss.action] }, [action])
											)
									),
									trailing && v('div', { classes: [themeCss.trailing], key: 'trailing' }, [trailing])
							  ])
							: null
					])
				]),
				...menupanes,
				has('use-alternate-header') ? v('div', { classes: [themeCss.divider] }, []) : null
			]
		);
	}
);
